
import React, { createContext, useContext } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { ProgressProvider, useProgress } from './ProgressContext';
import { InviteProvider, useInvites } from './InviteContext';
import { User, UserProgress, InviteStatus, OAuthProvider } from '@/types/user';

// Combined context type that matches the original UserContext
interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  userProgress: UserProgress;
  login: (userData: User, inviteCode?: string) => void;
  oauthLogin: (provider: OAuthProvider) => Promise<void>;
  logout: () => void;
  completeDay: (day: number, points: number) => void;
  isLoading: boolean;
  error: string | null;
  completeOnboarding: (preferences: { 
    nickname?: string, 
    notificationPreference?: 'email' | 'whatsapp' | 'both' | 'none',
    consentToDataTracking: boolean
  }) => void;
  invites: InviteStatus[];
  createInvite: (email: string) => Promise<InviteStatus>;
  revokeInvite: (code: string) => Promise<boolean>;
  validateInvite: (code: string) => Promise<boolean>;
  setAdminStatus: (isAdmin: boolean) => void;
}

// Create a context for backwards compatibility
const UserContext = createContext<UserContextType | undefined>(undefined);

// Combined provider that uses all our new providers
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProvider>
      <ProgressProvider>
        <InviteProvider>
          <CombinedProvider>{children}</CombinedProvider>
        </InviteProvider>
      </ProgressProvider>
    </AuthProvider>
  );
};

// Internal provider that combines all contexts
const CombinedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const progress = useProgress();
  const invites = useInvites();
  
  // Combine all contexts into one value
  const combinedValue: UserContextType = {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    userProgress: progress.userProgress,
    login: auth.login,
    oauthLogin: auth.oauthLogin,
    logout: auth.logout,
    completeDay: progress.completeDay,
    isLoading: auth.isLoading,
    error: auth.error,
    completeOnboarding: auth.completeOnboarding,
    invites: invites.invites,
    createInvite: invites.createInvite,
    revokeInvite: invites.revokeInvite,
    validateInvite: invites.validateInvite,
    setAdminStatus: auth.setAdminStatus
  };
  
  return (
    <UserContext.Provider value={combinedValue}>
      {children}
    </UserContext.Provider>
  );
};

// Hook that provides access to the combined context
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Export individual hooks for direct access
export { useAuth, useProgress, useInvites };
